import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { MiButton } from "@miplatform/components";
import { BUTTON_VARIANTS } from "@miplatform/constants";
import { IVerticalTabProps } from "@miplatform/types";
import { Box, Typography } from "@mui/material";
import { VerticalTabsSelector } from "./components/VerticalTabsSelector";

export function VerticalTabsLayout<GenericData extends FieldValues>(
  props: Readonly<IVerticalTabsLayoutViewProps<GenericData>>
) {
  const {
    tabs,
    value: controlledValue,
    setValue: setControlledValue,
    tabTitleGetter,
    tabSubtitleGetter,
    hideStatusIcons,
    onTabChange,
    onPreviousClick,
    onNextClick,
    renderLabel,
    extraLayoutProps,
    useLegacyStyles = false,
    contentStyles = {},
    layoutStyle = {},
    children,
    form,
    data,
    hideNavigationButtons = false,
    transparentBackground = false,
  } = props;

  const verticalSelectorContainerRef = useRef<HTMLDivElement>(null);
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState<string | number | null>(
    tabs.find((tab) => !tab.disabled && !tab.hidden)?.value ?? null
  );
  const selectedTabValue = controlledValue || value;

  const { flatTabs, currentTab, currentTabIndex } = useMemo(() => {
    const tabValue = controlledValue || value;
    const flatTabs = tabs
      .flatMap((tab) => [tab, ...(tab.subTabs || [])])
      .filter((tab) => !tab.hidden);
    const selectedTabIndex = flatTabs.findIndex(
      (item) => item.value === tabValue
    );
    if (selectedTabIndex !== -1) {
      return {
        flatTabs,
        currentTab: flatTabs[selectedTabIndex],
        currentTabIndex: selectedTabIndex,
      };
    }

    return { flatTabs, currentTab: null, currentTabIndex: -1 };
  }, [tabs, value, controlledValue]);

  const { classes } = useStyles({
    isFirstTabSelected: tabs.length > 0 && currentTab?.value === tabs[0]?.value,
    useLegacyStyles,
    contentStyles,
    layoutStyle,
    transparentBackground,
  });

  const handleTabSelect = useCallback(
    (item: IVerticalTabProps<GenericData> | null) => {
      if (controlledValue) {
        setControlledValue?.(item?.value ?? null);
      } else {
        setValue(item?.value ?? null);
      }

      if (onTabChange && item) {
        onTabChange(item);
      }
    },
    [onTabChange, controlledValue, setControlledValue]
  );

  const handleBack = useCallback(() => {
    if (currentTabIndex > 0) {
      (onPreviousClick || handleTabSelect)(flatTabs[currentTabIndex - 1]);
    }
  }, [currentTabIndex, flatTabs, onPreviousClick]);
  const handleNext = useCallback(() => {
    if (currentTabIndex < flatTabs.length - 1) {
      (onNextClick || handleTabSelect)(flatTabs[currentTabIndex + 1]);
    }
  }, [currentTabIndex, flatTabs, onNextClick]);

  useEffect(() => {
    if (tabs.length > 0 && !flatTabs.some((tab) => tab.value === value)) {
      // if removing data causes the selected tab to be removed, default back to first tab
      handleTabSelect(tabs[0]);
    }
  }, [tabs?.length, flatTabs, value, handleTabSelect]);

  return (
    <Box className={classes.verticalTabsLayoutViewContainer}>
      <Box
        className={classes.verticalTabsLayoutLeftSection}
        ref={verticalSelectorContainerRef}
      >
        <VerticalTabsSelector
          tabs={tabs}
          selectedTab={selectedTabValue}
          tabTitleGetter={tabTitleGetter}
          tabSubtitleGetter={tabSubtitleGetter}
          onTabSelect={handleTabSelect}
          hideStatusIcons={hideStatusIcons}
          useLegacyStyles={useLegacyStyles}
          renderLabel={renderLabel}
        />
      </Box>
      <Box className={classes.verticalTabsLayoutRightSection}>
        <Box className={classes.rightSectionContent} ref={contentContainerRef}>
          {children ??
            (currentTab && (
              <div key={currentTab.value} className={classes.innerContent}>
                <InternalConfigTabsView
                  key={currentTab.value}
                  selected={selectedTabValue === currentTab.value}
                >
                  {currentTab.viewComponent && !form && (
                    <currentTab.viewComponent
                      data={currentTab.data ?? data}
                      label={currentTab.label}
                      value={currentTab.value}
                      isComplete={currentTab.isComplete}
                      hasErrors={currentTab.hasErrors}
                      requiredPermissions={currentTab.requiredPermissions}
                      extraTabProps={currentTab.extraTabProps}
                      {...extraLayoutProps}
                    />
                  )}
                  {currentTab.controlledFormViewComponent && form && (
                    <currentTab.controlledFormViewComponent
                      data={currentTab.data ?? data ?? ({} as GenericData)}
                      label={currentTab.label}
                      value={currentTab.value}
                      isComplete={currentTab.isComplete}
                      hasErrors={currentTab.hasErrors}
                      requiredPermissions={currentTab.requiredPermissions}
                      form={form}
                      extraTabProps={currentTab.extraTabProps}
                      {...extraLayoutProps}
                    />
                  )}
                  {!currentTab.viewComponent &&
                    !currentTab.controlledFormViewComponent && (
                      <Typography variant="body1" color="textSecondary">
                        No view available for this tab.
                      </Typography>
                    )}
                </InternalConfigTabsView>
              </div>
            ))}
        </Box>
        {!hideNavigationButtons && (
          <Box className={classes.buttonContainer}>
            {currentTabIndex > 0 && (
              <MiButton
                title="Previous"
                variant={BUTTON_VARIANTS.OUTLINED}
                onClick={handleBack}
              />
            )}
            <MiButton
              title="Next"
              variant={BUTTON_VARIANTS.CONTAINED}
              onClick={handleNext}
              disabled={currentTabIndex === flatTabs.length - 1}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

const InternalConfigTabsView = ({
  children,
  selected,
}: {
  children: React.ReactNode;
  selected: boolean;
}) => {
  if (!selected) {
    return null;
  }

  return children;
};

interface IVerticalTabsLayoutViewProps<GenericData extends FieldValues> {
  data?: GenericData;
  value?: string | number | null;
  setValue?: (value: string | number | null) => void;
  tabs: IVerticalTabProps<GenericData>[];
  tabTitleGetter?: (item: IVerticalTabProps<GenericData>) => string;
  tabSubtitleGetter?: (item: IVerticalTabProps<GenericData>) => string;
  hideStatusIcons?: boolean;
  onTabChange?: (tab: IVerticalTabProps<GenericData>) => void;
  onPreviousClick?: (previousTab: IVerticalTabProps<GenericData>) => void;
  onNextClick?: (nextTab: IVerticalTabProps<GenericData>) => void;
  renderLabel?: (item: IVerticalTabProps<GenericData>) => React.ReactNode;
  /**
   * @property extraLayoutProps - Additional props to pass to each tab's view component (includes subTabs as well)
   */
  extraLayoutProps?: Record<string, unknown>;
  useLegacyStyles?: boolean;
  children?: React.ReactNode;
  contentStyles?: React.CSSProperties;
  layoutStyle?: React.CSSProperties;
  form?: UseFormReturn<GenericData>;
  hideNavigationButtons?: boolean;
  transparentBackground?: boolean;
}

const useStyles = makeStyles<{
  isFirstTabSelected: boolean;
  useLegacyStyles: boolean;
  contentStyles?: React.CSSProperties;
  layoutStyle?: React.CSSProperties;
  transparentBackground?: boolean;
}>()(
  (
    theme,
    {
      isFirstTabSelected,
      useLegacyStyles,
      contentStyles,
      layoutStyle,
      transparentBackground,
    }
  ) => ({
    verticalTabsLayoutLeftSection: {
      marginLeft: theme.spacing(3),
      ...(useLegacyStyles
        ? {}
        : {
            marginRight: theme.spacing(2),
          }),
    },
    verticalTabsLayoutViewContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "stretch",
      overflow: "hidden",
      backgroundColor: theme.palette.grey.lightAccent,
      height: "100%",
    },
    verticalTabsLayoutRightSection: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      overflowX: "hidden",
      overflowY: "auto",
      backgroundColor: transparentBackground
        ? "transparent"
        : theme.palette.background.default,
      width: "100%",
      margin: theme.spacing(3),
      marginLeft: 0,
      paddingBottom: theme.spacing(0.5),
      gap: theme.spacing(3),
      ...layoutStyle,
      ...(useLegacyStyles
        ? {
            borderTopRightRadius: theme.spacing(2),
            borderBottomRightRadius: theme.spacing(2),
            borderBottomLeftRadius: theme.spacing(2),
            borderTopLeftRadius: isFirstTabSelected ? 0 : theme.spacing(2),
          }
        : {
            borderRadius: theme.spacing(2),
          }),
    },
    rightSectionContent: {
      display: "flex",
      flex: 1,
      flexDirection: "column",
      padding: transparentBackground ? 0 : theme.spacing(4),
      height: transparentBackground ? "auto" : "100%",
      ...contentStyles,
    },
    buttonContainer: {
      display: "flex",
      gap: theme.spacing(2),
      padding: theme.spacing(2),
      borderRadius: 8,
      justifyContent: "flex-end",
      backgroundColor: theme.palette.common.white,
      boxShadow: theme.shadows[1],
      marginTop: "auto",
      justifySelf: "flex-end",
      position: "sticky",
      minHeight: 54,
      bottom: -4,
      zIndex: 2,
    },
    innerContent: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
      flex: 1,
      minHeight: 0,
    },
  })
);
